import { useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
// @mui
import {
  Button,
  Box,
  Checkbox,
  Divider,
  Grid,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material'
import { ChevronLeft } from '@mui/icons-material'
import InfoIcon from '@mui/icons-material/Info'
// components
import {
  StyledBaseContainer,
  StyledTextValue,
} from '~/commons/styled'
import VerificationConfirmationDialog from './VerificationConfirmationDialog'
import AlertDialog from '~/commons/components/Dialog'
import StateImage from 'components/state-image'
// hooks
import { useBoundBalanceStore } from 'domains/Balance/store'
import { useBoundVerificationUserStore } from 'domains/Users/Verification/stores'
import { useSnackbar } from 'notistack'
// helpers
import { getErrorMessage, isConflictError } from 'helpers/error'
import { stripPhoneDialCode } from 'helpers/formats/phone'
// services
import { putBankAccountKYC, submitKYC } from '~/services/user'
import useUpdateBankAccount from 'hooks/domains/FinancialApproval/useUpdateBankAccount'
import { UpdateBankAccountResponse } from '~/services/financial/interface'
import { useFeatureFlags } from 'helpers/feature-flag'
// constants
import { TERMS_CONDITION_URL } from '~/config'
import appConfig from 'configs/app'
import { BankAccountStatus } from 'domains/Users/Verification/contants'
import OTPDialog from 'commons/containers/OTPDialog'
import { PATH_DASHBOARD } from 'routes/paths'
import useAuth from 'hooks/useAuth'
import useSession from 'hooks/commons/useSession'
import { useAuthStore } from 'domains/Users/Auth/store'

const EditBankAccountDialog = dynamic(
  () => import('../../components/EditBankAccountDialog'),
)
import trackEvent from '~/trackers'
import useLocales from 'hooks/useLocales'
import { SNACKBAR_ANCHOR } from 'constants/snackbar'
import { FEATURE_FLAGS } from 'constants/feature-flags'

export default function PreviewForm({
  title = 'key_date_summary',
  subtitle = 'key_double_check_and_make_sure_the_data',
  badge = null,
  headerEndAdornment = null,
  customFooter = null,
  showDefaultCTA = true,
  reducedOpacity = false,
  isEdit = false,
  setActiveStepEditBank,
}) {
  const { t } = useLocales()
  const { sendOTP, user } = useAuth()
  const { canPerform, isOwner } = useSession()
  // The old PUT /kyc/bank edit steps up with an OTP (see onSubmitEditBankAccount
  // below); the new POST /financial/bank-account has no OTP step at all — it
  // steps up via Owner-approval instead (WAITING_APPROVAL), so there's nothing
  // for the OTP dialog to gate when this is on.
  const isMultipleRolesEnabled = Boolean(
    useFeatureFlags(FEATURE_FLAGS.multipleRoles),
  )

  // Sight of the KYC record itself — the creator's identity and tax documents.
  //
  // Owner-only, and deliberately NOT derived from kyc:view. That action grants
  // reaching this page, which is what a member needs to manage the payout
  // account; the identity documents behind it are the creator's own and are not
  // delegable, so no grantable permission can open them. Everyone else sees the
  // bank account section and nothing more.
  const canViewKycRecord = isOwner
  const updateBankAccountMutation = useUpdateBankAccount()
  // The POST /financial/bank-account response, held so the result dialog can
  // render from it. Two shapes: an Owner's change applies immediately
  // (EXECUTED), anyone else's waits on Owner approval (WAITING_APPROVAL) and
  // carries a BE-written user-facing `message`.
  const [bankAccountResult, setBankAccountResult] =
    useState<UpdateBankAccountResponse | null>(null)
  const [isAgreementAccepted, setAgreementValue] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmationDialogOpened, openConfirmationDialog] =
    useState(false)
  const [isOTPDialogOpened, openOTPDialog] = useState(false)
  const [
    isEditBankAccountDialogOpen,
    setIsEditBankAccountDialogOpen,
  ] = useState(false)

  const { enqueueSnackbar } = useSnackbar()
  const { isReady, push, query, pathname } = useRouter()
  const { setActiveStep, personalData, tempBankAccount } =
    useBoundVerificationUserStore()
  const trackerData = { query, pathname, user }

  const { getBankList, bankList } = useBoundBalanceStore((state) => ({
    bankList: state.bankList,
    getBankList: state.getBankList,
    isLoadingBankList: state.isLoadingBankList,
  }))
  const { verificationMethod, setVerificationMethod } = useAuthStore(
    (state) => ({
      verificationMethod: state.verificationMethod,
      setVerificationMethod: state.setVerificationMethod,
    }),
  )

  const reducedOpacitySx = {
    sx: { opacity: reducedOpacity ? 0.5 : 1 },
  }

  useEffect(() => {
    if (isReady) {
      setVerificationMethod('WA')
      getBankList()
      trackEvent.user('visit_review_document', trackerData)
    }
  }, [isReady])

  const selectedBank = bankList?.find(
    (bank) =>
      bank.bank_id ===
      (isEdit ? tempBankAccount.bank_id : personalData?.bank_id),
  )

  const hasEmail = personalData?.email
  const npwpType =
    personalData?.npwp_type === 'PRIBADI'
      ? t('key_personal').toUpperCase()
      : t('key_company').toUpperCase()

  const isBankAccountVerified = useMemo(
    () =>
      personalData?.bank_account_status ===
      BankAccountStatus.VERIFIED,
    [personalData],
  )

  const onSubmitEditBankAccount = async (code) => {
    try {
      setIsLoading(true)
      const payload = {
        ...tempBankAccount,
        bank_id: Number(tempBankAccount.bank_id),
        verify_otp: {
          username: user?.phone,
          delivery_method: verificationMethod,
          action: 'VERIFY_KYC',
          otp: Number(code),
        },
      }

      const result = await putBankAccountKYC(payload)
      push(PATH_DASHBOARD.general.verification.root)
      return Promise.resolve(result)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      setIsLoading(false)
    }
  }

  // POST /financial/bank-account path — no OTP step-up, so this submits
  // directly from the Preview step rather than going through openOTPDialog.
  // Field names are renamed (bank_id -> new_bank_id, etc.) rather than
  // reusing tempBankAccount's shape as-is: the new endpoint's fields are all
  // explicitly the *new* value, since the BE now also needs to show the
  // *current* bank alongside it in the Owner-approval email.
  const onSubmitEditBankAccountViaFinancialApproval = async () => {
    try {
      setIsLoading(true)
      const result = await updateBankAccountMutation.mutateAsync({
        new_bank_id: Number(tempBankAccount.bank_id),
        new_bank_account_no: tempBankAccount.bank_account_no,
        new_bank_account_name: tempBankAccount.bank_account_name,
      })
      // Shown in a dialog rather than a snackbar: for a non-Owner this is not
      // "saved", it's "submitted and waiting on someone else", which needs
      // more explanation than a toast can carry — and it mirrors the withdraw
      // flow's result dialog (Figma node 636-51577).
      setBankAccountResult(result)
    } catch (error) {
      // 409 CONFLICT means a change is already waiting on Owner approval. The
      // BE's own message for it is generic, and getErrorMessage would surface
      // that, so name the actual situation: nothing here succeeds until the
      // pending request is decided.
      enqueueSnackbar(
        isConflictError(error)
          ? t('key_bank_account_change_already_requested')
          : getErrorMessage(error),
        { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR },
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseBankAccountResult = () => {
    setBankAccountResult(null)
    push(PATH_DASHBOARD.general.verification.root)
  }

  const onActionButtonSubmit = async () => {
    try {
      trackEvent.user('submit_kyc_verification', trackerData, {
        full_name: personalData?.full_name,
        email: personalData?.email,
        phone_number: personalData?.auth_phone,
        bank_name: personalData?.bank_account_name,
        bank_id: personalData?.bank_id,
        id_card_type: personalData?.id_card_type,
        id_verification_method:
          personalData?.id_card_type === 1 ? 'ktp' : 'paspor',
      })
      setIsLoading(true)
      if (isEdit) {
        if (isMultipleRolesEnabled) {
          await onSubmitEditBankAccountViaFinancialApproval()
        } else {
          openOTPDialog(true)
        }
      } else {
        if (isBankAccountVerified) {
          openOTPDialog(true)
        } else {
          const payload = {
            is_agreement_accepted: Boolean(isAgreementAccepted),
          }
          const result = await submitKYC(payload)
          if (result?.data?.code === 'SUCCESS') {
            openConfirmationDialog(true)
          }
        }
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const requestOtp = async (method) => {
    try {
      const phoneWithoutCode = stripPhoneDialCode(user?.phone)
      await sendOTP(phoneWithoutCode, method, 'VERIFY_KYC')
    } catch (error) {
      enqueueSnackbar(error, { variant: 'error' })
    }
  }

  return (
    <>
      <StyledBaseContainer>
        <Grid container spacing={2}>
          <Grid
            item
            gap={0.5}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
          >
            <Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="h6">{t(title)}</Typography>
                {badge}
              </Stack>
              <StyledTextValue
                variant="body2"
                sx={(theme) => ({ color: theme.palette.grey[600] })}
              >
                {t(subtitle)}
              </StyledTextValue>
            </Stack>
            {headerEndAdornment}
          </Grid>
          {/* The KYC record proper — the creator's personal legal identity.
              Opening the page must not reveal it: a member granted kyc:view to
              manage the payout account still sees only the bank section, since
              these documents are the creator's own. The trailing Divider goes
              with it: with these hidden the bank section is first, and a rule
              directly under the header reads as a stray line. */}
          {canViewKycRecord && (
            <>
            <Grid container item spacing={1.5} {...reducedOpacitySx}>
              <Grid item>
                <Typography
                  variant="overline"
                  sx={(theme) => ({ color: theme.palette.grey[600] })}
                >
                  {t('key_personal_data')}
                </Typography>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={12}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_full_name')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.full_name}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_phone_number')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {`+${personalData?.auth_phone}`}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_email')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.email}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Typography
                  variant="overline"
                  sx={(theme) => ({ color: theme.palette.grey[600] })}
                >
                  {t('key_address_according_to_identity')}
                </Typography>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Typography
                  variant="overline"
                  sx={(theme) => ({ color: theme.palette.grey[600] })}
                >
                  {t('key_address_according_domicile')}
                </Typography>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_province')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.address_province}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_province')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.residence_address_province}
                  </StyledTextValue>
                </Stack>
              </Grid>

              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_city_district')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.address_district}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_city_district')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.residence_address_district}
                  </StyledTextValue>
                </Stack>
              </Grid>

              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_subdistrict')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.address_sub_district}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_subdistrict')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.residence_address_sub_district}
                  </StyledTextValue>
                </Stack>
              </Grid>

              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_urban_village')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.address_village}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_urban_village')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.residence_address_village}
                  </StyledTextValue>
                </Stack>
              </Grid>

              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_postal_code')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.address_postal_code}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_postal_code')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.residence_address_postal_code}
                  </StyledTextValue>
                </Stack>
              </Grid>

              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_address_according_to_identity')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.address}
                  </StyledTextValue>
                </Stack>
              </Grid>
              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={6}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ color: theme.palette.grey[600] })}
                  >
                    {t('key_address_according_domicile')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {personalData?.residence_address}
                  </StyledTextValue>
                </Stack>
              </Grid>
            </Grid>

            <Grid container item spacing={1.5} {...reducedOpacitySx}>
              <Grid item>
                <Typography
                  variant="overline"
                  sx={(theme) => ({ color: theme.palette.grey[600] })}
                >
                  {t('key_document')}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              item
              gap={1}
              display="flex"
              flexDirection="column"
              xs={6}
              {...reducedOpacitySx}
            >
              <Stack gap={0.2}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.grey[600] })}
                >
                  {personalData?.id_card_type === 1
                    ? t('key_ktp_number')
                    : t('key_passport_number')}
                </Typography>
                <StyledTextValue variant="overline">
                  {personalData?.id_card}
                </StyledTextValue>
              </Stack>
            </Grid>
            <Grid
              item
              gap={1}
              display="flex"
              flexDirection="column"
              xs={6}
              {...reducedOpacitySx}
            >
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.palette.grey[600] })}
              >
                {t('key_selfie_with_KTP', {
                  KTP:
                    personalData?.id_card_type === 1
                      ? t('key_ID_card')
                      : t('key_passport'),
                })}
              </Typography>
            </Grid>
            <Grid
              item
              gap={1}
              display="flex"
              flexDirection="column"
              xs={12}
              sm={6}
              {...reducedOpacitySx}
            >
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  justifyContent: 'center',
                  border: `1px dashed ${theme.palette.grey[400]}`,
                  borderRadius: '12px',
                  padding: '8px',
                })}
              >
                <img
                  src={personalData?.id_card_photo}
                  alt=""
                  style={{
                    height: '163px',
                    objectFit: 'contain',
                    borderRadius: '12px',
                  }}
                />
              </Box>
            </Grid>
            <Grid
              item
              gap={1}
              display="flex"
              flexDirection="column"
              xs={12}
              sm={6}
              {...reducedOpacitySx}
            >
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  justifyContent: 'center',
                  border: `1px dashed ${theme.palette.grey[400]}`,
                  borderRadius: '12px',
                  padding: '8px',
                })}
              >
                <img
                  src={personalData?.selfie_photo}
                  alt=""
                  style={{
                    height: '163px',
                    objectFit: 'contain',
                    borderRadius: '12px',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            </>
          )}
          <Grid container item spacing={1.5} {...reducedOpacitySx}>
            <Grid item>
              <Typography
                variant="overline"
                sx={(theme) => ({ color: theme.palette.grey[600] })}
              >
                {t('key_bank_account')}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            item
            gap={1}
            display="flex"
            flexDirection="column"
            xs={6}
            sm={isBankAccountVerified ? 3 : 4}
            {...reducedOpacitySx}
          >
            <Stack gap={0.2}>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.palette.grey[600] })}
              >
                {t('key_bank_name')}
              </Typography>
              <StyledTextValue variant="overline">
                {selectedBank?.bank_name || ''}
              </StyledTextValue>
            </Stack>
          </Grid>
          <Grid
            item
            gap={1}
            display="flex"
            flexDirection="column"
            xs={6}
            sm={isBankAccountVerified ? 3 : 4}
            {...reducedOpacitySx}
          >
            <Stack gap={0.2}>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.palette.grey[600] })}
              >
                {t('key_bank_account_number')}
              </Typography>
              <StyledTextValue variant="overline">
                {isEdit
                  ? tempBankAccount.bank_account_no
                  : personalData?.bank_account_no}
              </StyledTextValue>
            </Stack>
          </Grid>
          <Grid
            item
            gap={1}
            display="flex"
            flexDirection="column"
            xs={12}
            sm={4}
            {...reducedOpacitySx}
          >
            <Stack gap={0.2}>
              <Typography
                variant="body2"
                sx={(theme) => ({ color: theme.palette.grey[600] })}
              >
                {t('key_account_holder_name')}
              </Typography>
              <StyledTextValue variant="overline">
                {isEdit
                  ? tempBankAccount.bank_account_name
                  : personalData?.bank_account_name}
                {(isEdit
                  ? tempBankAccount.is_different_name
                  : personalData?.is_different_name) && (
                  <Tooltip
                    title="Terdapat perbedaan nama identitas dan nama pemilik rekening"
                    placement="bottom"
                    arrow
                  >
                    <InfoIcon
                      color="info"
                      sx={{ fontSize: '16px', ml: '2px', mb: '-2px' }}
                    />
                  </Tooltip>
                )}
              </StyledTextValue>
            </Stack>
          </Grid>
          {isBankAccountVerified && !isEdit && (
            <Grid
              item
              gap={1}
              display="flex"
              flexDirection="column"
              xs={12}
              sm={2}
              {...reducedOpacitySx}
            >
              {/* Reaching this page needs kyc:view, so changing what is on it
                  needs kyc:edit. */}
              {canPerform('kyc:edit') && (
                <Button
                  id="b-user-edit-bank-account"
                  onClick={() =>
                    isMultipleRolesEnabled
                      ? push(PATH_DASHBOARD.general.verification.edit_bank_account)
                      : setIsEditBankAccountDialogOpen(true)
                  }
                  variant="outlined"
                >
                  {t('key_edit')}
                </Button>
              )}
            </Grid>
          )}
          {/* Tax identity, same reasoning as the personal data above. */}
          {canViewKycRecord && (
            <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid container item spacing={1.5}>
              <Grid
                item
                xs={12}
                sx={{
                  opacity: reducedOpacity ? 0.5 : 1,
                }}
              >
                <StyledTextValue variant="overline">
                  {`NPWP ${npwpType}`}
                </StyledTextValue>
              </Grid>

              {personalData.id_card_type === 1 &&
                personalData.npwp_type === 'PRIBADI' && (
                  <Grid
                    item
                    gap={1}
                    display="flex"
                    flexDirection="column"
                    xs={6}
                    sx={{
                      opacity: reducedOpacity ? 0.5 : 1,
                    }}
                  >
                    <Stack gap={0.2}>
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          color: theme.palette.grey[600],
                        })}
                      >
                        NIK
                      </Typography>
                      <StyledTextValue variant="overline">
                        {personalData.id_card}
                      </StyledTextValue>
                    </Stack>
                  </Grid>
                )}

              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={
                  personalData.id_card_type === 1 &&
                  personalData.npwp_type === 'PRIBADI'
                    ? 6
                    : 12
                }
                sx={{
                  opacity: reducedOpacity ? 0.5 : 1,
                }}
              >
                <Stack gap={0.2}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color: theme.palette.grey[600],
                    })}
                  >
                    {t('key_NPWP_number')}
                  </Typography>
                  <StyledTextValue variant="overline">
                    {`${personalData.npwp_number || '-'}`}
                  </StyledTextValue>
                </Stack>
              </Grid>

              <Grid
                item
                gap={1}
                display="flex"
                flexDirection="column"
                xs={12}
                sm={6}
                sx={{
                  opacity: reducedOpacity ? 0.5 : 1,
                }}
              >
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    justifyContent: 'center',
                    border: `1px dashed ${theme.palette.grey[400]}`,
                    borderRadius: '12px',
                    padding: '8px',
                  })}
                >
                  <img
                    src={personalData.npwp_photo}
                    alt=""
                    style={{
                      height: '163px',
                      objectFit: 'contain',
                      borderRadius: '12px',
                    }}
                  />
                </Box>
              </Grid>

            </Grid>
            </>
          )}

          {/* Outside the KYC gate on purpose: Verify Now is disabled until
              this is ticked, and it used to sit inside the section only an
              Owner sees — leaving everyone else on the summary with no way to
              agree. */}
          {customFooter || (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" alignItems="center">
                  <Checkbox
                    id="b-user-accept-terms"
                    name="is_agreement_accepted"
                    checked={isAgreementAccepted}
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement>,
                    ) => {
                      trackEvent.user(
                        'click_tnc_agreement',
                        trackerData,
                      )
                      setAgreementValue(event.target.checked)
                    }}
                  />
                  <Typography variant="body2">
                    {t('key_i_agree_with')}{' '}
                    <Typography
                      id="l-users-previewform-terms"
                      role="link"
                      variant="button"
                      onClick={() => {
                        window.open(
                          `${TERMS_CONDITION_URL}#Aplikasi,-Akun-dan-Keamanan`,
                          '_blank',
                        )
                      }}
                      sx={(theme) => ({
                        color: theme.palette.info.main,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      })}
                    >
                      {t('key_tiptip_terms_and_condition')}
                    </Typography>
                  </Typography>
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </StyledBaseContainer>
      {showDefaultCTA && (
        <Stack
          sx={{ marginTop: '12px' }}
          justifyContent="space-between"
          alignItems="center"
          direction="row"
        >
          <Button
            id="b-user-back-to-dashboard"
            startIcon={<ChevronLeft />}
            sx={(theme) => ({
              color: theme.palette.grey[800],
              ':hover': {
                background: 'transparent',
                opacity: 0.7,
              },
            })}
            onClick={() => {
              if (isEdit) {
                setActiveStepEditBank(0)
              } else {
                setActiveStep(2)
              }
            }}
          >
            {t('key_back')}
          </Button>
          <Button
            id="b-user-verify-now"
            variant="contained"
            sx={{ marginLeft: '8px' }}
            size="large"
            disabled={!isAgreementAccepted || isLoading}
            onClick={onActionButtonSubmit}
          >
            {t('key_verify_now')}
          </Button>
        </Stack>
      )}
      <VerificationConfirmationDialog
        open={isConfirmationDialogOpened}
        openDialog={openConfirmationDialog}
        hasEmail={hasEmail}
      />
      <OTPDialog
        open={isOTPDialogOpened}
        onClose={() => openOTPDialog(false)}
        onRequestOTP={(verificationMethod) =>
          requestOtp(verificationMethod)
        }
        onVerify={(code) => onSubmitEditBankAccount(code)}
      />
      <EditBankAccountDialog
        isOpen={isEditBankAccountDialogOpen}
        onClickCancel={() => setIsEditBankAccountDialogOpen(false)}
      />
      {/* Result of POST /financial/bank-account. Only the flag-on path can
          produce it, so the old OTP flow is unaffected. Closing and the CTA
          both land on Verifikasi Data — where this flow started — rather than
          leaving the user on a stale summary of data they just changed. */}
      <AlertDialog
        open={Boolean(bankAccountResult)}
        onClose={handleCloseBankAccountResult}
        header={<></>}
        body={
          bankAccountResult ? (
            // component="span" throughout: AlertDialog renders `body` inside a
            // DialogContentText (<p>), and a <div>/<h5> in a <p> is invalid
            // nesting — the browser force-closes the paragraph, which breaks
            // the layout and leaves a stray background behind the title.
            <Stack
              component="span"
              direction="column"
              spacing={2}
              sx={{ display: 'flex', textAlign: 'center', mt: '24px' }}
            >
              <Box component="span" sx={{ display: 'block' }}>
                <StateImage
                  src={`${appConfig.assetBaseUrl}/webp/illustrations/illustration_components.webp`}
                  sx={{ height: '180px', margin: 'auto' }}
                />
              </Box>
              <Typography
                variant="h5"
                component="span"
                sx={{ display: 'block' }}
              >
                {t(
                  bankAccountResult.status === 'WAITING_APPROVAL'
                    ? 'key_bank_account_pending_approval_title'
                    : 'key_bank_account_update_success_title',
                )}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{ display: 'block' }}
              >
                {bankAccountResult.status === 'WAITING_APPROVAL'
                  ? // BE-provided, already Indonesian and user-facing — don't
                    // re-derive a message from `status`.
                    bankAccountResult.message
                  : t('key_bank_account_update_success')}
              </Typography>
            </Stack>
          ) : (
            <></>
          )
        }
        footer={
          // minHeight rather than a fixed height + matching lineHeight: the
          // label wraps on narrow dialogs and in longer locales, and a pinned
          // 48px line-height pushed the second line straight out of the button.
          <Button
            id="b-user-bank-account-result-back"
            variant="contained"
            sx={{
              width: '90%',
              minHeight: '48px',
              margin: '24px auto',
            }}
            onClick={handleCloseBankAccountResult}
          >
            {t('key_back_to_verification_page')}
          </Button>
        }
      />
    </>
  )
}
